import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil, forkJoin } from 'rxjs';
import { 
  ScheduledActivity, 
  ActivityType,
  BulkOperationResult 
} from '../../interfaces/activity-schedule.interface';
import { ActivityScheduleService } from '../../services/activity-schedule.service';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';

export interface BulkOperation {
  type: 'reschedule' | 'cancel' | 'complete' | 'assign' | 'change_type' | 'add_notes' | 'export';
  label: string;
  icon: string;
  description: string;
  requiresForm: boolean;
  confirmationRequired: boolean;
  dangerLevel: 'low' | 'medium' | 'high';
}

export interface BulkOperationData {
  operation: string;
  selectedActivities: number[];
  formData?: any;
  reason?: string;
}

@Component({
  selector: 'app-bulk-operations',
  templateUrl: './bulk-operations.component.html',
  styleUrls: ['./bulk-operations.component.scss']
})
export class BulkOperationsComponent implements OnInit, OnDestroy {
  @Input() activities: ScheduledActivity[] = [];
  @Input() selectedActivities: number[] = [];
  @Output() operationCompleted = new EventEmitter<BulkOperationResult>();
  @Output() selectionChanged = new EventEmitter<number[]>();
  
  private destroy$ = new Subject<void>();
  
  // State
  isProcessing = false;
  currentUserId = 1;
  showOperationModal = false;
  currentOperation: BulkOperation | null = null;
  
  // Forms
  bulkForm!: FormGroup;
  
  // Configuration
  bulkOperations: BulkOperation[] = [
    {
      type: 'reschedule',
      label: 'Reprogramează',
      icon: 'event_repeat',
      description: 'Reprogramează activitățile selectate pentru o altă dată/oră',
      requiresForm: true,
      confirmationRequired: true,
      dangerLevel: 'medium'
    },
    {
      type: 'cancel',
      label: 'Anulează',
      icon: 'cancel',
      description: 'Anulează activitățile selectate cu specificarea motivului',
      requiresForm: true,
      confirmationRequired: true,
      dangerLevel: 'high'
    },
    {
      type: 'complete',
      label: 'Marchează ca finalizate',
      icon: 'check_circle',
      description: 'Marchează activitățile selectate ca finalizate cu succes',
      requiresForm: true,
      confirmationRequired: true,
      dangerLevel: 'low'
    },
    {
      type: 'assign',
      label: 'Reasignează',
      icon: 'assignment_ind',
      description: 'Reasignează activitățile selectate către alt operator',
      requiresForm: true,
      confirmationRequired: true,
      dangerLevel: 'medium'
    },
    {
      type: 'change_type',
      label: 'Schimbă tipul',
      icon: 'swap_horiz',
      description: 'Schimbă tipul activităților selectate',
      requiresForm: true,
      confirmationRequired: true,
      dangerLevel: 'medium'
    },
    {
      type: 'add_notes',
      label: 'Adaugă notițe',
      icon: 'note_add',
      description: 'Adaugă notițe sau comentarii la activitățile selectate',
      requiresForm: true,
      confirmationRequired: false,
      dangerLevel: 'low'
    },
    {
      type: 'export',
      label: 'Exportă selectate',
      icon: 'download',
      description: 'Exportă activitățile selectate în format Excel sau PDF',
      requiresForm: true,
      confirmationRequired: false,
      dangerLevel: 'low'
    }
  ];
  
  // Data
  activityTypes: ActivityType[] = [];
  availableOperators: any[] = [];

  constructor(
    private fb: FormBuilder,
    private activityScheduleService: ActivityScheduleService,
    private authService: AuthService,
    private notificationService: NotificationService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.initializeUser();
    this.initializeForm();
    this.loadSupportingData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async initializeUser(): Promise<void> {
    try {
      const user = await this.authService.getCurrentUser();
      if (user && user.id_user) {
        this.currentUserId = user.id_user;
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  }

  private initializeForm(): void {
    this.bulkForm = this.fb.group({
      // Reschedule fields
      newDate: [''],
      newStartTime: [''],
      newEndTime: [''],
      rescheduleReason: [''],
      
      // Cancel fields
      cancelReason: ['', Validators.required],
      
      // Complete fields
      completionNotes: [''],
      completionDate: [new Date().toISOString().split('T')[0]],
      
      // Assignment fields
      newOperatorId: ['', Validators.required],
      assignmentNotes: [''],
      
      // Type change fields
      newActivityTypeId: ['', Validators.required],
      typeChangeReason: [''],
      
      // Notes fields
      additionalNotes: ['', Validators.required],
      notesCategory: ['general'],
      
      // Export fields
      exportFormat: ['excel', Validators.required],
      includeDetails: [true],
      includeHistory: [false]
    });
  }

  private loadSupportingData(): void {
    // Load activity types
    this.activityScheduleService.getActivityTypes().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (types) => {
        this.activityTypes = types;
      },
      error: (error) => {
        console.error('Error loading activity types:', error);
      }
    });
    
    // Load available operators (mock data for now)
    this.availableOperators = [
      { id: 1, name: 'Operator 1', department: 'Protecția Copilului' },
      { id: 2, name: 'Operator 2', department: 'Asistență Socială' },
      { id: 3, name: 'Operator 3', department: 'Psihologie' }
    ];
  }

  // Selection management
  isAllSelected(): boolean {
    return this.activities.length > 0 && this.selectedActivities.length === this.activities.length;
  }

  isPartiallySelected(): boolean {
    return this.selectedActivities.length > 0 && this.selectedActivities.length < this.activities.length;
  }

  toggleSelectAll(): void {
    if (this.isAllSelected()) {
      this.selectedActivities = [];
    } else {
      this.selectedActivities = this.activities.map(activity => activity.id);
    }
    this.selectionChanged.emit(this.selectedActivities);
  }

  toggleActivitySelection(activityId: number): void {
    const index = this.selectedActivities.indexOf(activityId);
    if (index > -1) {
      this.selectedActivities.splice(index, 1);
    } else {
      this.selectedActivities.push(activityId);
    }
    this.selectionChanged.emit(this.selectedActivities);
  }

  isActivitySelected(activityId: number): boolean {
    return this.selectedActivities.includes(activityId);
  }

  // Bulk operations
  canExecuteOperation(operation: BulkOperation): boolean {
    if (this.selectedActivities.length === 0) return false;
    
    const selectedActivitiesData = this.activities.filter(a => 
      this.selectedActivities.includes(a.id)
    );
    
    switch (operation.type) {
      case 'reschedule':
        return selectedActivitiesData.every(a => 
          ['scheduled', 'rescheduled'].includes(a.status)
        );
      case 'cancel':
        return selectedActivitiesData.every(a => 
          !['completed', 'cancelled'].includes(a.status)
        );
      case 'complete':
        return selectedActivitiesData.every(a => 
          ['scheduled', 'rescheduled'].includes(a.status)
        );
      case 'assign':
      case 'change_type':
      case 'add_notes':
        return true;
      case 'export':
        return true;
      default:
        return false;
    }
  }

  startBulkOperation(operation: BulkOperation): void {
    if (!this.canExecuteOperation(operation)) {
      this.notificationService.warningSwal(
        'Operație indisponibilă',
        'Operația nu poate fi executată pentru activitățile selectate.',
        'warning'
      );
      return;
    }
    
    this.currentOperation = operation;
    this.resetForm(operation.type);
    
    if (operation.requiresForm) {
      this.showOperationModal = true;
    } else {
      this.executeOperation();
    }
  }

  private resetForm(operationType: string): void {
    this.bulkForm.reset();
    
    // Set default values based on operation type
    switch (operationType) {
      case 'reschedule':
        this.bulkForm.patchValue({
          newDate: new Date().toISOString().split('T')[0]
        });
        break;
      case 'complete':
        this.bulkForm.patchValue({
          completionDate: new Date().toISOString().split('T')[0]
        });
        break;
      case 'export':
        this.bulkForm.patchValue({
          exportFormat: 'excel',
          includeDetails: true,
          includeHistory: false
        });
        break;
    }
    
    this.updateFormValidators(operationType);
  }

  private updateFormValidators(operationType: string): void {
    // Clear all validators first
    Object.keys(this.bulkForm.controls).forEach(key => {
      this.bulkForm.get(key)?.clearValidators();
    });
    
    // Add validators based on operation type
    switch (operationType) {
      case 'reschedule':
        this.bulkForm.get('newDate')?.setValidators([Validators.required]);
        this.bulkForm.get('newStartTime')?.setValidators([Validators.required]);
        this.bulkForm.get('newEndTime')?.setValidators([Validators.required]);
        this.bulkForm.get('rescheduleReason')?.setValidators([Validators.required]);
        break;
      case 'cancel':
        this.bulkForm.get('cancelReason')?.setValidators([Validators.required, Validators.minLength(10)]);
        break;
      case 'assign':
        this.bulkForm.get('newOperatorId')?.setValidators([Validators.required]);
        break;
      case 'change_type':
        this.bulkForm.get('newActivityTypeId')?.setValidators([Validators.required]);
        break;
      case 'add_notes':
        this.bulkForm.get('additionalNotes')?.setValidators([Validators.required, Validators.minLength(5)]);
        break;
      case 'export':
        this.bulkForm.get('exportFormat')?.setValidators([Validators.required]);
        break;
    }
    
    // Update validity
    Object.keys(this.bulkForm.controls).forEach(key => {
      this.bulkForm.get(key)?.updateValueAndValidity();
    });
  }

  executeOperation(): void {
    if (!this.currentOperation) return;
    
    const operation = this.currentOperation;
    
    // Validate form if required
    if (operation.requiresForm && !this.bulkForm.valid) {
      this.notificationService.warningSwal(
        'Formular incomplet',
        'Vă rugăm să completați toate câmpurile obligatorii.',
        'error'
      );
      return;
    }
    
    // Show confirmation if required
    if (operation.confirmationRequired) {
      const message = this.getConfirmationMessage(operation);
      this.notificationService.warningSwal(
        'Confirmare operație',
        message,
        'warning'
      ).then((result) => {
        if (result.isConfirmed) {
          this.processBulkOperation();
        }
      });
    } else {
      this.processBulkOperation();
    }
  }

  private processBulkOperation(): void {
    if (!this.currentOperation) return;
    
    this.isProcessing = true;
    const operation = this.currentOperation;
    
    const operationData: BulkOperationData = {
      operation: operation.type,
      selectedActivities: this.selectedActivities,
      formData: this.bulkForm.value
    };
    
    // Execute the appropriate operation
    let operationObservable;
    
    switch (operation.type) {
      case 'reschedule':
        operationObservable = this.executeRescheduleOperation(operationData);
        break;
      case 'cancel':
        operationObservable = this.executeCancelOperation(operationData);
        break;
      case 'complete':
        operationObservable = this.executeCompleteOperation(operationData);
        break;
      case 'assign':
        operationObservable = this.executeAssignOperation(operationData);
        break;
      case 'change_type':
        operationObservable = this.executeChangeTypeOperation(operationData);
        break;
      case 'add_notes':
        operationObservable = this.executeAddNotesOperation(operationData);
        break;
      case 'export':
        operationObservable = this.executeExportOperation(operationData);
        break;
      default:
        this.isProcessing = false;
        this.notificationService.warningSwal('Eroare', 'Operație necunoscută', 'error');
        return;
    }
    
    operationObservable.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: BulkOperationResult) => {
        this.isProcessing = false;
        this.showOperationModal = false;
        this.currentOperation = null;
        
        this.operationCompleted.emit(result);
        
        this.notificationService.successSwal(
          'Operație completă',
          `Operația a fost executată cu succes pentru ${result.successCount} activități.`
        );
        
        // Clear selection after successful operation
        this.selectedActivities = [];
        this.selectionChanged.emit(this.selectedActivities);
      },
      error: (error) => {
        console.error('Bulk operation error:', error);
        this.isProcessing = false;
        this.notificationService.warningSwal('Eroare', 'Eroare la executarea operației', 'error');
      }
    });
  }

  private executeRescheduleOperation(data: BulkOperationData) {
    const formData = data.formData;
    return this.activityScheduleService.bulkRescheduleActivities(
      data.selectedActivities,
      formData.newDate,
      formData.newStartTime,
      formData.newEndTime,
      formData.rescheduleReason
    );
  }

  private executeCancelOperation(data: BulkOperationData) {
    return this.activityScheduleService.bulkCancelActivities(
      data.selectedActivities,
      data.formData.cancelReason
    );
  }

  private executeCompleteOperation(data: BulkOperationData) {
    return this.activityScheduleService.bulkCompleteActivities(
      data.selectedActivities,
      data.formData.completionNotes,
      data.formData.completionDate
    );
  }

  private executeAssignOperation(data: BulkOperationData) {
    return this.activityScheduleService.bulkAssignActivities(
      data.selectedActivities,
      data.formData.newOperatorId,
      data.formData.assignmentNotes
    );
  }

  private executeChangeTypeOperation(data: BulkOperationData) {
    return this.activityScheduleService.bulkChangeActivityType(
      data.selectedActivities,
      data.formData.newActivityTypeId,
      data.formData.typeChangeReason
    );
  }

  private executeAddNotesOperation(data: BulkOperationData) {
    return this.activityScheduleService.bulkAddNotes(
      data.selectedActivities,
      data.formData.additionalNotes,
      data.formData.notesCategory
    );
  }

  private executeExportOperation(data: BulkOperationData) {
    return this.activityScheduleService.bulkExportActivities(
      data.selectedActivities,
      data.formData.exportFormat,
      data.formData.includeDetails,
      data.formData.includeHistory
    );
  }

  private getConfirmationMessage(operation: BulkOperation): string {
    const count = this.selectedActivities.length;
    const activityWord = count === 1 ? 'activitate' : 'activități';
    
    switch (operation.type) {
      case 'reschedule':
        return `Sunteți sigur că doriți să reprogramați ${count} ${activityWord} selectată/e?`;
      case 'cancel':
        return `Sunteți sigur că doriți să anulați ${count} ${activityWord} selectată/e? Această acțiune nu poate fi anulată.`;
      case 'complete':
        return `Sunteți sigur că doriți să marcați ca finalizate ${count} ${activityWord} selectată/e?`;
      case 'assign':
        return `Sunteți sigur că doriți să reasignați ${count} ${activityWord} selectată/e?`;
      case 'change_type':
        return `Sunteți sigur că doriți să modificați tipul pentru ${count} ${activityWord} selectată/e?`;
      default:
        return `Sunteți sigur că doriți să executați această operație pentru ${count} ${activityWord} selectată/e?`;
    }
  }

  closeModal(): void {
    this.showOperationModal = false;
    this.currentOperation = null;
  }

  getOperationIcon(operation: BulkOperation): string {
    return operation.icon;
  }

  getOperationClass(operation: BulkOperation): string {
    const baseClass = 'btn me-2 mb-2';
    
    if (!this.canExecuteOperation(operation)) {
      return `${baseClass} btn-outline-secondary disabled`;
    }
    
    switch (operation.dangerLevel) {
      case 'high':
        return `${baseClass} btn-danger`;
      case 'medium':
        return `${baseClass} btn-warning`;
      case 'low':
      default:
        return `${baseClass} btn-primary`;
    }
  }

  get selectedCount(): number {
    return this.selectedActivities.length;
  }

  get hasSelection(): boolean {
    return this.selectedActivities.length > 0;
  }

  get selectedActivitiesData(): ScheduledActivity[] {
    return this.activities.filter(a => this.selectedActivities.includes(a.id));
  }

  // Helper methods for template
  trackByActivityId(index: number, activity: ScheduledActivity): number {
    return activity.id;
  }

  getActivityTypeIcon(activity: ScheduledActivity): string {
    // Return appropriate icon based on activity type
    return 'event';
  }

  getStatusClass(status: string): string {
    return `bg-${status}`;
  }

  getStatusLabel(status: string): string {
    const statusOptions = this.activityScheduleService.getStatusOptions();
    const option = statusOptions.find(opt => opt.key === status);
    return option?.label || status;
  }

  getAssignedLabel(activity: ScheduledActivity): string {
    return activity.assigned_user_id ? `User ${activity.assigned_user_id}` : 'Neasignat';
  }
}